// ----------------------------------------------------------------------

import { localStorageAvailable } from './storage-available';

/**
 * Generic local storage utility functions for storing and retrieving typed data
 */

export interface StorageOptions {
  /** Default value to return if storage is not available or key doesn't exist */
  defaultValue?: any;
  /** Expiration time in milliseconds. If set, the item will expire after this duration */
  ttl?: number;
}

interface StorageItem<T> {
  value: T;
  timestamp: number;
  ttl?: number;
}

/**
 * Stores a value in localStorage with optional TTL (time to live)
 * @param key - The storage key
 * @param value - The value to store (will be JSON stringified)
 * @param options - Storage options including TTL
 */
export function setStorageItem<T>(key: string, value: T, options?: StorageOptions): void {
  if (!localStorageAvailable()) {
    console.warn('localStorage is not available');
    return;
  }

  try {
    const storageItem: StorageItem<T> = {
      value,
      timestamp: Date.now(),
      ttl: options?.ttl,
    };

    localStorage.setItem(key, JSON.stringify(storageItem));
  } catch (error) {
    console.error(`Error storing item with key "${key}":`, error);
  }
}

/**
 * Retrieves a value from localStorage with optional type checking and TTL validation
 * @param key - The storage key
 * @param options - Storage options including default value
 * @returns The stored value or the default value
 */
export function getStorageItem<T>(key: string, options?: StorageOptions): T {
  if (!localStorageAvailable()) {
    return options?.defaultValue;
  }

  try {
    const storedData = localStorage.getItem(key);

    if (!storedData) {
      return options?.defaultValue;
    }

    const parsedData: StorageItem<T> = JSON.parse(storedData);

    // Check if the item has expired (if TTL was set)
    if (parsedData.ttl && parsedData.timestamp) {
      const now = Date.now();
      const expirationTime = parsedData.timestamp + parsedData.ttl;

      if (now > expirationTime) {
        // Item has expired, remove it and return default value
        removeStorageItem(key);
        return options?.defaultValue;
      }
    }

    return parsedData.value;
  } catch (error) {
    console.error(`Error retrieving item with key "${key}":`, error);
    // If there's an error parsing, try to clean up the corrupted entry
    removeStorageItem(key);
    return options?.defaultValue;
  }
}

/**
 * Removes an item from localStorage
 * @param key - The storage key to remove
 */
export function removeStorageItem(key: string): void {
  if (!localStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing item with key "${key}":`, error);
  }
}

/**
 * Checks if a key exists in localStorage and is not expired
 * @param key - The storage key to check
 * @returns True if the key exists and is valid, false otherwise
 */
export function hasStorageItem(key: string): boolean {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    const storedData = localStorage.getItem(key);

    if (!storedData) {
      return false;
    }

    const parsedData: StorageItem<any> = JSON.parse(storedData);

    // Check if the item has expired (if TTL was set)
    if (parsedData.ttl && parsedData.timestamp) {
      const now = Date.now();
      const expirationTime = parsedData.timestamp + parsedData.ttl;

      if (now > expirationTime) {
        // Item has expired, remove it
        removeStorageItem(key);
        return false;
      }
    }

    return true;
  } catch (error) {
    // If there's an error, assume the item doesn't exist or is corrupted
    removeStorageItem(key);
    return false;
  }
}

/**
 * Clears all items from localStorage (use with caution)
 */
export function clearAllStorage(): void {
  if (!localStorageAvailable()) {
    return;
  }

  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
}

// ----------------------------------------------------------------------
// Forecast-specific storage utilities
// ----------------------------------------------------------------------

export const STORAGE_KEYS = {
  FORECAST_WEEKS: 'forecast_weeks_into_future',
  FORECAST_TIME_RANGE: 'forecast_time_range',
  STOCK_IMPACT_ENABLED: 'athory.stockImpact.enabled',
  TOUR_STATE: 'athory.tour.state',
} as const;

export interface ForecastPreferences {
  weeksIntoFuture: number;
  timeRange?: string;
}

/**
 * Stores forecast length preference
 * @param weeks - Number of weeks into future
 */
export function setForecastWeeks(weeks: number): void {
  setStorageItem(STORAGE_KEYS.FORECAST_WEEKS, weeks);
}

/**
 * Retrieves stored forecast length preference
 * @param defaultWeeks - Default value if not stored
 * @returns Stored weeks or default value
 */
export function getForecastWeeks(defaultWeeks: number = 12): number {
  return getStorageItem<number>(STORAGE_KEYS.FORECAST_WEEKS, { defaultValue: defaultWeeks });
}

/**
 * Stores complete forecast preferences
 * @param preferences - Forecast preferences object
 */
export function setForecastPreferences(preferences: ForecastPreferences): void {
  setStorageItem(STORAGE_KEYS.FORECAST_WEEKS, preferences.weeksIntoFuture);

  if (preferences.timeRange) {
    setStorageItem(STORAGE_KEYS.FORECAST_TIME_RANGE, preferences.timeRange);
  }
}

/**
 * Retrieves complete forecast preferences
 * @param defaultWeeks - Default weeks value
 * @returns Forecast preferences object
 */
export function getForecastPreferences(defaultWeeks: number = 12): ForecastPreferences {
  return {
    weeksIntoFuture: getForecastWeeks(defaultWeeks),
    timeRange: getStorageItem<string>(STORAGE_KEYS.FORECAST_TIME_RANGE, { defaultValue: 'ALL' }),
  };
}

// ----------------------------------------------------------------------
// PO Stock-Impact gauge — per-user toggle
// ----------------------------------------------------------------------

/**
 * Stores the PO Stock-Impact gauge enabled flag (per-browser, per-user).
 */
export function setStockImpactEnabled(enabled: boolean): void {
  setStorageItem(STORAGE_KEYS.STOCK_IMPACT_ENABLED, enabled);
}

/**
 * Retrieves the PO Stock-Impact gauge enabled flag. Defaults to ON.
 */
export function getStockImpactEnabled(defaultEnabled: boolean = true): boolean {
  return getStorageItem<boolean>(STORAGE_KEYS.STOCK_IMPACT_ENABLED, {
    defaultValue: defaultEnabled,
  });
}
